import { useState } from 'react';
import {
  Card, CardBody, Badge, Alert, Button, ButtonGroup, FormGroup, Label, Input,
  Nav, NavItem, NavLink, TabContent, TabPane,
} from 'reactstrap';
import { PageHeader } from '../../components/PageHeader';
import { DataTable } from '../../components/DataTable';
import { RelativeTime } from '../../components/RelativeTime';
import { HasPermission } from '../../rbac/HasPermission';
import { useHasPermission } from '../../rbac/useHasPermission';
import {
  usePulseAlerts,
  useAckPulseAlert,
  usePulseSecurityEvents,
  usePulseDecisions,
} from './usePulseSecurity';

const SEVERITY_COLORS = { low: 'secondary', medium: 'info', high: 'warning', critical: 'danger' };

function SeverityBadge({ severity }) {
  return <Badge color={SEVERITY_COLORS[severity] ?? 'secondary'} pill>{severity}</Badge>;
}

function AlertsTab() {
  const [unackOnly, setUnackOnly] = useState(true);
  const canWrite = useHasPermission({ perms: ['integrations.write'] });
  const query = usePulseAlerts({ unacknowledgedOnly: unackOnly });
  const ack = useAckPulseAlert();
  const items = query.data?.items ?? [];

  return (
    <div>
      <Card className="mb-3">
        <CardBody className="py-2">
          <FormGroup switch className="mb-0">
            <Input type="switch" checked={unackOnly} onChange={(e) => setUnackOnly(e.target.checked)} id="pulse-unack-switch" />
            <Label check for="pulse-unack-switch">Nur unbearbeitete anzeigen</Label>
          </FormGroup>
        </CardBody>
      </Card>
      <DataTable
        loading={query.isLoading}
        error={query.error}
        empty={!query.isLoading && items.length === 0}
        emptyMessage="Keine Alerts für die aktuellen Filter."
        onRetry={() => query.refetch()}
      >
        <thead>
          <tr>
            <th style={{ width: 100 }}>Severity</th>
            <th>Titel</th>
            <th>Quelle</th>
            <th>Host</th>
            <th style={{ width: 130 }}>Erstellt</th>
            <th style={{ width: 130 }}>Status</th>
            <th style={{ width: 120 }} className="text-end">Aktionen</th>
          </tr>
        </thead>
        <tbody>
          {items.map((a) => (
            <tr key={a.id}>
              <td><SeverityBadge severity={a.severity} /></td>
              <td>
                {a.title}
                {a.message && <div className="small text-muted text-truncate" style={{ maxWidth: 360 }} title={a.message}>{a.message}</div>}
              </td>
              <td className="small font-monospace">{a.source}</td>
              <td className="small text-muted">{a.hostname ?? '—'}</td>
              <td><RelativeTime iso={a.created_at} /></td>
              <td>
                {a.acknowledged_at
                  ? <Badge color="success" pill>bestätigt</Badge>
                  : <Badge color="warning" pill>offen</Badge>}
              </td>
              <td className="text-end">
                {canWrite && !a.acknowledged_at && (
                  <Button size="sm" color="success" outline onClick={() => ack.mutateAsync({ id: a.id })}>
                    ✓ Ack
                  </Button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </DataTable>
    </div>
  );
}

function EventsTab() {
  const [source, setSource] = useState('');
  const query = usePulseSecurityEvents({ source });
  const items = query.data?.items ?? [];

  return (
    <div>
      <Card className="mb-3">
        <CardBody className="py-2">
          <FormGroup className="mb-0" style={{ maxWidth: 220 }}>
            <Label className="small text-muted mb-1">Quelle</Label>
            <Input type="select" bsSize="sm" value={source} onChange={(e) => setSource(e.target.value)}>
              <option value="">Alle</option>
              <option value="core">core</option>
              <option value="nexus">nexus</option>
              <option value="orbit">orbit</option>
              <option value="mtff">mtff</option>
            </Input>
          </FormGroup>
        </CardBody>
      </Card>
      <DataTable
        loading={query.isLoading}
        error={query.error}
        empty={!query.isLoading && items.length === 0}
        emptyMessage="Keine Security-Events."
        onRetry={() => query.refetch()}
      >
        <thead>
          <tr>
            <th style={{ width: 100 }}>Severity</th>
            <th>Event-Type</th>
            <th>Quelle</th>
            <th>IP</th>
            <th style={{ width: 130 }}>Zeitpunkt</th>
          </tr>
        </thead>
        <tbody>
          {items.map((e) => (
            <tr key={e.id}>
              <td><SeverityBadge severity={e.severity} /></td>
              <td className="small font-monospace">{e.event_type}</td>
              <td className="small font-monospace">{e.source}</td>
              <td className="small text-muted">{e.ip ?? '—'}</td>
              <td><RelativeTime iso={e.created_at} /></td>
            </tr>
          ))}
        </tbody>
      </DataTable>
    </div>
  );
}

function DecisionsTab() {
  const query = usePulseDecisions({ activeOnly: true });
  const items = query.data?.items ?? [];

  return (
    <DataTable
      loading={query.isLoading}
      error={query.error}
      empty={!query.isLoading && items.length === 0}
      emptyMessage="Keine aktiven CrowdSec-Decisions."
      onRetry={() => query.refetch()}
    >
      <thead>
        <tr>
          <th style={{ width: 90 }}>Type</th>
          <th>Scenario</th>
          <th>Ziel</th>
          <th style={{ width: 110 }}>Dauer</th>
          <th style={{ width: 130 }}>Bis</th>
        </tr>
      </thead>
      <tbody>
        {items.map((d) => (
          <tr key={d.id}>
            <td><Badge color={d.type === 'ban' ? 'danger' : 'warning'} pill>{d.type}</Badge></td>
            <td className="small font-monospace">{d.scenario}</td>
            <td className="small">{d.scope}: <code>{d.value}</code></td>
            <td className="small text-muted">{d.duration}</td>
            <td><RelativeTime iso={d.untilAt} /></td>
          </tr>
        ))}
      </tbody>
    </DataTable>
  );
}

/**
 * Pulse Security-Dashboard — Alerts (CrowdSec/Zabbix/Business-Metriken),
 * rohe Security-Events aus CORE/Nexus/Orbit/MTFF-Emittern, und die
 * gespiegelten CrowdSec-Decisions.
 */
export function PulseSecurityPage() {
  const [tab, setTab] = useState('alerts');
  return (
    <div>
      <PageHeader title="Pulse · Security-Dashboard" subtitle="Alerts, Security-Events, CrowdSec-Decisions" />
      <Alert color="info" className="small">
        Pulse sammelt Sicherheits-Events aus CORE/Nexus/Orbit/MTFF sowie
        CrowdSec- und Zabbix-Alarme zentral. Auto-Refresh alle 20-30s.
      </Alert>
      <Nav tabs className="mb-3">
        <NavItem><NavLink active={tab === 'alerts'} onClick={() => setTab('alerts')} style={{ cursor: 'pointer' }}>Alerts</NavLink></NavItem>
        <NavItem><NavLink active={tab === 'events'} onClick={() => setTab('events')} style={{ cursor: 'pointer' }}>Security-Events</NavLink></NavItem>
        <NavItem><NavLink active={tab === 'decisions'} onClick={() => setTab('decisions')} style={{ cursor: 'pointer' }}>CrowdSec-Decisions</NavLink></NavItem>
      </Nav>
      <TabContent activeTab={tab}>
        <TabPane tabId="alerts">{tab === 'alerts' && <AlertsTab />}</TabPane>
        <TabPane tabId="events">{tab === 'events' && <EventsTab />}</TabPane>
        <TabPane tabId="decisions">{tab === 'decisions' && <DecisionsTab />}</TabPane>
      </TabContent>
    </div>
  );
}
